import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthMethod, AuthMode } from '../models';

/**
 * Auth API Service
 * Handles registration and profile creation via backend API
 */
@Injectable({
  providedIn: 'root'
})
export class AuthApiService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Register new user - creates both Firebase Auth user and PostgreSQL profile
   * @param email User email
   * @param password User password
   * @param firstName User first name
   * @param lastName User last name
   * @returns Promise with registration result
   */
  async register(
    email: string,
    password: string,
    firstName: string,
    lastName: string
  ): Promise<RegisterResponse> {
    const response = this.http.post<RegisterResponse>(
      `${this.apiUrl}/auth/register`,
      {
        email,
        password,
        firstName,
        lastName
      }
    );

    return firstValueFrom(response);
  }

  /**
   * Sync newly created Firebase user to PostgreSQL
   * Creates the initial profile record
   * @returns Promise with sync result
   */
  async syncProfile(): Promise<any> {
    const response = this.http.post(
      `${this.apiUrl}/auth/sync-profile`,
      {}
    );

    return firstValueFrom(response);
  }

  /**
   * Register the signed-in Firebase user with a new organization in a
   * single Hub transaction. Mirrors the mobile signup path
   * (CropSwipe → `POST /auth/register-with-organization`).
   *
   * Web only allows `role: 'pilot'` — farmer signup is mobile-only and
   * the web signup UI never reaches this method for the farmer role.
   * Address fields are sent only for pilots; farmers add their org
   * location during onboarding.
   */
  async registerWithOrganization(
    body: RegisterWithOrganizationRequest,
  ): Promise<RegisterWithOrganizationResponse> {
    const response = this.http.post<RegisterWithOrganizationResponse>(
      `${this.apiUrl}/auth/register-with-organization`,
      body,
    );

    return firstValueFrom(response);
  }

  /**
   * Record a completed login on the backend.
   *
   * Web auth happens client-side via Firebase, so the Hub never sees a
   * login. This pings POST /auth/record-login after sign-in so the Hub can
   * stamp lastLogin / lastLoginLocation (platform inferred from Referer).
   */
  async recordLogin(): Promise<any> {
    const response = this.http.post(
      `${this.apiUrl}/auth/record-login`,
      {}
    );

    return firstValueFrom(response);
  }

  async recordFailedLoginAttempt(email: string): Promise<any> {
    const response = this.http.post(
      `${this.apiUrl}/auth/login-failed`,
      { email: email}
    );

    return firstValueFrom(response);
  }

  /**
   * Resend the verification email for the signed-in user. Goes through the Hub
   * so it uses our templates and /auth-action links; the account is identified
   * from the bearer token, so there's no body.
   */
  async resendVerificationEmail(): Promise<any> {
    const response = this.http.post(
      `${this.apiUrl}/auth/email-codes/verification`,
      {}
    );

    return firstValueFrom(response);
  }

  async verifyEmail(email: string): Promise<any> {
    const response = this.http.post(
      `${this.apiUrl}/auth/sync-email-verified`,
      {email}
    );
    return firstValueFrom(response);
  }
  
  /**
   * Update user profile
   * @param profileData Profile data to update
   * @returns Promise with update result
   */
  async updateProfile(profileData: Partial<ProfileData>): Promise<any> {
    const response = this.http.put(
      `${this.apiUrl}/auth/profile`,
      profileData
    );

    return firstValueFrom(response);
  }

  /**
   * Sends the password-reset email containing the six-digit code. The Hub
   * returns generic success for unknown addresses, so a resolved promise
   * doesn't imply the account exists.
   */
  async sendResetPassword(email: string): Promise<any> {
    const response = this.http.post(
      `${this.apiUrl}/auth/email-codes/reset`,
      { email }
    );

    return firstValueFrom(response);
  } 

  async sendEmailLoginCode(
    email: string,
  ): Promise<SendLoginCodeResponse> {
    const response = this.http.post<SendLoginCodeResponse>(
      `${this.apiUrl}/auth/email-codes/login`,
      { email }
    )
    return firstValueFrom(response);
  }

  async getResetActionCode(): Promise<any> {
    const response = this.http.post(
      `${this.apiUrl}/auth/action-codes/reset`,
      {} 
    )
    return firstValueFrom(response);
  }

  /**
   * Exchanges a typed six-digit code for the Firebase `oobCode` behind it,
   * returned as `data`.
   *
   * `mode` picks which flow's code to redeem — 'reset' feeds
   * `confirmPasswordReset`, 'verify' feeds `applyActionCode`. The Hub matches
   * it exactly, so a code from one flow won't satisfy the other.
   */
  async verifyAuthEmailCode(
    email: string,
    code: string,
    mode: AuthMode,
  ): Promise<VerifyAuthEmailCodeResponse> {
    const response = this.http.post<VerifyAuthEmailCodeResponse>(
      `${this.apiUrl}/auth/email-codes/redeem`,
      {email, code, mode}
    );
    return firstValueFrom(response);
  }

  //Verifies a reset code when the user arrives from the link in a reset email
  async verifyResetCodeWithId(
    codeId: string,
    code: string,
  ): Promise<VerifyAuthEmailCodeResponse> {
    const response = this.http.post<VerifyAuthEmailCodeResponse>(
      `${this.apiUrl}/auth/email-codes/${codeId}/redeem`,
      { code }
    );
    return firstValueFrom(response);
  }
}


export interface SendLoginCodeResponse {
  success: boolean;
}

/**
 * Hub response from `POST /auth/email-codes/redeem`. `data` carries the
 * Firebase oobCode on success.
 */
export interface VerifyAuthEmailCodeResponse {
  success: boolean;
  message?: string;
  data?: string;
  error?: string;
  email?: string;
}

/**
 * Registration response from backend
 */
export interface RegisterResponse {
  success: boolean;
  message: string;
  userId: string;
  verificationLink?: string;
  error?: string;
}

/**
 * Body for `POST /auth/register-with-organization`. Mirrors mobile
 * `AuthProvider.register` payload (lib/providers/auth_provider.dart).
 */
export interface RegisterWithOrganizationRequest {
  role: 'pilot' | 'farmer';
  firstName: string;
  lastName: string;
  phone: string;
  organizationName: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
}

/**
 * Hub response after a successful register-with-organization call.
 */
export interface RegisterWithOrganizationResponse {
  success: boolean;
  profileId: string;
  organizationId: string;
  customClaims?: unknown;
  error?: string;
}

/**
 * Profile data structure
 */
export interface ProfileData {
  firstName?: string;
  lastName?: string;
  phone?: string;
  dateOfBirth?: string;
  entityType?: string;
  entityName?: string;
  taxId?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  hasPart107?: boolean;
  part107Number?: string;
  part107Expiration?: string;
  hasCommercialLicense?: boolean;
  licenseNumber?: string;
  emailVerified?: boolean;
}
